import React, {useState, useRef, useEffect} from 'react';
import styles from './ExpandableDropdown.module.css';
import qaList from "./qalist";
import scienceContentList from "./scienceContentList";
import WebAudioTTS from "../../app/webaudiotts.js";
import axios from 'axios';
import {getHostIP} from "../../networking/Hosts";

const ExpandableDropdown = ({chatType, wechatWarningVisible}) => {
    const currentHostIP = getHostIP();
    
    const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
    const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
    const [isVideoVisible, setIsVideoVisible] = useState(false);
    const [isArticleVisible, setIsArticleVisible] = useState(false);
    const [hasBeenClicked, setHasBeenClicked] = useState(false); // 记录是否已经点击过
    
    // TTS相关状态
    const [isPlaying, setIsPlaying] = useState(false);
    const [isSynthesizing, setIsSynthesizing] = useState(false);
    const [currentPlayingAnswer, setCurrentPlayingAnswer] = useState(null);
    const [playbackProgress, setPlaybackProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [tencentCredentials, setTencentCredentials] = useState(null);
    const webAudioTTSRef = useRef(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null); // 当前选中的答案（用于弹窗显示）

    const {article, video} = scienceContentList[chatType] || {};
    const currentQaList = qaList[chatType] || [];

    const toggleDropdown = () => {
        setIsDropdownExpanded(!isDropdownExpanded);
        // 标记已经点击过
        if (!hasBeenClicked) {
            setHasBeenClicked(true);
        }
    };

    // 获取腾讯云临时密钥
    const getTencentToken = async () => {
        try {
            const response = await axios.get(currentHostIP + '/api/tencent/token', {
                withCredentials: true
            });
            if (response.data.success) {
                const tokenData = {
                    ...response.data.data,
                    appid: 1328869042
                };
                setTencentCredentials(tokenData);
                return tokenData;
            }
        } catch (error) {
            console.error('获取腾讯云token失败:', error);
        }
        return null;
    };

    // 初始化时获取token
    useEffect(() => {
        getTencentToken();
    }, [currentHostIP]);

    // 清理文本，移除HTML标签、参考文献和"信息来源"，但保留换行符
    const cleanTextForTTS = (text) => {
        if (!text) return '';
        let cleanText = text;
        // 先将HTML换行标签转换为换行符
        cleanText = cleanText.replace(/<br\s*\/?>/gi, '\n');
        cleanText = cleanText.replace(/<\/p>/gi, '\n');
        cleanText = cleanText.replace(/<\/div>/gi, '\n');
        // 移除其他HTML标签
        cleanText = cleanText.replace(/<[^>]*>/g, '');
        // 移除参考文献标记
        cleanText = cleanText.replace(/\[\d+\]/g, '');
        // 移除"信息来源"及其后面的内容
        const infoSourceIndex = cleanText.indexOf('信息来源');
        if (infoSourceIndex !== -1) {
            cleanText = cleanText.substring(0, infoSourceIndex);
        }
        // 清理多余的空白行，但保留单个换行符
        cleanText = cleanText.replace(/\n{3,}/g, '\n\n');
        return cleanText.trim();
    };

    // 开始TTS播放
    const startTTSPlayback = async (questionText, answerText, answerIndex) => {
        if (!WebAudioTTS) {
            console.error('TTS SDK未加载');
            return;
        }

        let credentials = tencentCredentials;
        if (!credentials) {
            credentials = await getTencentToken();
            if (!credentials) {
                return;
            }
        }

        // 停止当前播放
        stopTTSPlayback();

        setIsSynthesizing(true);
        setIsPlaying(false);
        setCurrentPlayingAnswer(answerIndex);
        setPlaybackProgress(0);
        setCurrentTime(0);
        setTotalDuration(0);

        const params = {
            secretid: credentials.credentials.tmpSecretId,
            secretkey: credentials.credentials.tmpSecretKey,
            token: credentials.credentials.sessionToken,
            appid: credentials.appid,
            voice_type: 502003,
            speed: 0,
            volume: 0,
        };

        try {
            const tts = new WebAudioTTS(currentHostIP, params, true);
            webAudioTTSRef.current = tts;

            // 设置回调
            tts.OnSynthesisStart = () => {
                setIsSynthesizing(true);
            };

            tts.OnProgressUpdate = (progress, currentTimeValue, durationValue) => {
                setPlaybackProgress(progress);
                setCurrentTime(currentTimeValue || 0);
                setTotalDuration(durationValue || 0);
            };

            tts.OnDurationLoaded = (duration) => {
                setTotalDuration(duration || 0);
            };

            tts.OnPlaybackStart = () => {
                setIsSynthesizing(false);
                setIsPlaying(true);
            };

            tts.OnPlaybackComplete = () => {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingAnswer(null);
                setPlaybackProgress(0);
                setCurrentTime(0);
                setTotalDuration(0);
            };

            tts.OnPlaybackStopped = () => {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingAnswer(null);
                setPlaybackProgress(0);
                setCurrentTime(0);
                setTotalDuration(0);
            };

            const cleanText = cleanTextForTTS(answerText);
            if (!cleanText) {
                setIsPlaying(false);
                setIsSynthesizing(false);
                setCurrentPlayingAnswer(null);
                return;
            }

            await tts.start(cleanText);

        } catch (error) {
            console.error('启动TTS失败:', error);
            setIsPlaying(false);
            setIsSynthesizing(false);
            setCurrentPlayingAnswer(null);
        }
    };

    // 停止TTS播放
    const stopTTSPlayback = () => {
        if (webAudioTTSRef.current) {
            webAudioTTSRef.current.stop();
            webAudioTTSRef.current = null;
        }
        setIsPlaying(false);
        setIsSynthesizing(false);
        setCurrentPlayingAnswer(null);
        setPlaybackProgress(0);
        setCurrentTime(0);
        setTotalDuration(0);
    };

    // 格式化时间
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // 点击问题按钮，弹出答案
    const handleQuestionClick = (index) => {
        setSelectedAnswer(index);
        setExpandedQuestionIndex(null); // 关闭原来的展开
    };

    // 关闭答案弹窗
    const closeAnswerModal = () => {
        setSelectedAnswer(null);
        stopTTSPlayback(); // 关闭时停止播放
    };

    const showVideo = () => {
        setIsVideoVisible(true);
    };

    const closeVideo = () => {
        setIsVideoVisible(false);
    };

    const showArticle = () => {
        setIsArticleVisible(true);
    };

    const closeArticle = () => {
        setIsArticleVisible(false);
    };

    return (
        <>
            <div className={`${styles.dropdownWrapper} ${chatType === 'HPV' ? styles.hpvDropdown : ''} ${isDropdownExpanded ? styles.expanded : ''} ${!hasBeenClicked && !wechatWarningVisible ? styles.peekAnimation : ''}`}>
                <button className={styles.dropdownButton} onClick={toggleDropdown}>
                    {isDropdownExpanded ? '收起' : `必备知识`}
                </button>
                <div className={`${styles.dropdownContent} ${isDropdownExpanded ? styles.expanded : ''}`}>
                    <div className={styles.scienceButtons}>
                        {video && <button className={styles.scienceButton} onClick={showVideo}>看科普视频</button>}
                        {article && <button className={styles.scienceButton} onClick={showArticle}>看科普文章</button>}
                    </div>
                    <div className={styles.answerHint}>
                        💡 点击下方问题按钮查看详细答案
                    </div>
                    {currentQaList.map((qa, index) => (
                        <button
                            key={index}
                            className={styles.questionButton}
                            onClick={() => handleQuestionClick(index)}
                        >
                            {qa.question}
                        </button>
                    ))}
                </div>
            </div>
            {/* 视频弹窗 */}
            {isVideoVisible && video && (
                <div className={styles.videoOverlay}>
                    <div className={styles.videoWrapper}>
                        <button className={styles.closeButton} onClick={closeVideo}>×</button>
                        <iframe
                            title="video"
                            src={video}
                            scrolling="no"
                            frameBorder="no"
                            allowFullScreen="true"
                        ></iframe>
                    </div>
                </div>
            )}

            {/* 文章弹窗 */}
            {isArticleVisible && article && (
                <div className={styles.articleOverlay}>
                    <button className={styles.articleCloseButton} onClick={closeArticle}>×</button>
                    <div className={styles.articleWrapper}>
                        {chatType === 'HPV' ? (
                            <img className={styles.hpvArticle} src={article} alt="HPV科普文章" />
                        ) : (
                            <iframe
                                title={`${chatType}科普文章`}
                                src={article}
                                scrolling="auto"
                                frameBorder="no"
                                allowFullScreen="true"
                            ></iframe>
                        )}
                    </div>
                </div>
            )}

            {/* 答案弹窗 */}
            {selectedAnswer !== null && currentQaList[selectedAnswer] && (
                <div className={styles.answerOverlay} onClick={closeAnswerModal}>
                    <div className={styles.answerModal} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.answerCloseButton} onClick={closeAnswerModal}>×</button>
                        <div className={styles.answerHeader}>
                            <h3 className={styles.answerQuestion}>
                                {currentQaList[selectedAnswer].question}
                            </h3>
                            {/* 朗读按钮和进度条 */}
                            <div className={styles.answerTTSContainer}>
                                <button
                                    className={`${styles.answerTTSButton} ${currentPlayingAnswer === selectedAnswer && isPlaying ? styles.answerTTSButtonActive : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (currentPlayingAnswer === selectedAnswer && isPlaying) {
                                            stopTTSPlayback();
                                        } else {
                                            startTTSPlayback(currentQaList[selectedAnswer].question , currentQaList[selectedAnswer].answer, selectedAnswer);
                                        }
                                    }}
                                    disabled={isSynthesizing && currentPlayingAnswer === selectedAnswer}
                                >
                                    {isSynthesizing && currentPlayingAnswer === selectedAnswer ? '生成中...' : 
                                     currentPlayingAnswer === selectedAnswer && isPlaying ? '⏹️ 停止' : '🔊 朗读'}
                                </button>
                                {/* 进度条 */}
                                {currentPlayingAnswer === selectedAnswer && !isSynthesizing && (
                                    <div className={styles.answerProgressContainer}>
                                        <div 
                                            className={styles.answerProgressBar}
                                            style={{ width: `${playbackProgress}%` }}
                                        />
                                        <div className={styles.answerProgressTime}>
                                            {formatTime(currentTime)} / {formatTime(totalDuration)}
                                        </div>
                                    </div>
                                )}
                                {/* 合成中指示器 */}
                                {isSynthesizing && currentPlayingAnswer === selectedAnswer && (
                                    <div className={styles.answerSynthesizing}>
                                        <span>正在生成语音...</span>
                                        <div className={styles.answerSynthesizingDots}>
                                            <span></span><span></span><span></span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.answerContent}>
                            <div dangerouslySetInnerHTML={{__html: currentQaList[selectedAnswer].answer}}/>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ExpandableDropdown;
